import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import Slider from "react-slick";
import {fieldObjectSectionStyle} from "../../helpers/graphQLShortcuts";
import {fieldObjectSectionStyleCss} from "../../helpers/createStyles";
import Paragraph from "../Paragraph";
import {filterHeadline, filterHTML} from "../../helpers/filters";
import './FeatureCardsAlt.scss';

interface Data {
    paragraphById: {
        fieldVarDisplayType: string;
        fieldVarItemsPerRow: string;
        fieldInnerFeaturedItems: Array<{targetRevisionId: string}>;
        fieldInnerFeatSectionHeader: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldVarTextAlignment: string;
            }>
        }>;
        fieldInnerFeatSectionFooter: Array<{
            entity: Array<{
                fieldTxtHeadline: string;
                fieldHtmlBody: Array<{
                    value: string;
                    format: string;
                    processed: string;
                }>;
                fieldObjectCta: Array<{targetRevisionId: string}>;
            }>
        }>;
        fieldInnerFeatSectionStyle: Array<{entity: Array}>;
        fieldCardMinimumHeight: number;
    }
}

class FeatureCardsQuery extends Query<Data, {id: string}> {}

const GET_CARDS = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    ... on ParagraphComponentFeatCardContainer2 {
      fieldVarDisplayType
      fieldVarItemsPerRow
      fieldCardMinimumHeight
      fieldInnerFeaturedItems {
        targetRevisionId
      }
      fieldInnerFeatSectionHeader {
        ... on FieldParagraphFieldInnerFeatSectionHeader {
          entity {
            ... on Paragraph {
              ... on ParagraphInnerSectionHeader {
                fieldTxtHeadline
                fieldHtmlBody {
                  value
                  format
                  processed
                }
                fieldVarTextAlignment
              }
            }
          }
        }
      }
      fieldInnerFeatSectionFooter {
        ... on FieldParagraphFieldInnerFeatSectionFooter {
          entity {
            ... on Paragraph {
              ... on ParagraphInnerSectionFooter {
                fieldTxtHeadline
                fieldHtmlBody {
                  value
                  format
                  processed
                }
                fieldObjectCta {
                  targetRevisionId
                }
              }
            }
          }
        }
      }
      fieldInnerFeatSectionStyle {
        ... on FieldParagraphFieldInnerFeatSectionStyle {
          ${fieldObjectSectionStyle}
        }
      }
    }
  }
}
`;

const FeatureCardsAlt = ({id, markComponentLoadedAs, handleLoadedComponents}) => (
    <FeatureCardsQuery asyncMode query={GET_CARDS} variables={{ id }}>
        {({ loading, error, data }) => {
            if (loading) return null;
            if (error) {
                markComponentLoadedAs(id, true);
                return <div>{error}</div>;
            }

            const field = data.paragraphById;
            const header = field.fieldInnerFeatSectionHeader ? field.fieldInnerFeatSectionHeader.entity : null;
            const footer = field.fieldInnerFeatSectionFooter ? field.fieldInnerFeatSectionFooter.entity : null;

            const styles = fieldObjectSectionStyleCss(field.fieldInnerFeatSectionStyle, `${field.fieldVarDisplayType} ${field.fieldVarDisplayType}--items-${field.fieldVarItemsPerRow}`);

            if (!styles.css.padding) {
                styles.css.padding = '80px 20px';
            }

            markComponentLoadedAs(id, true);

            return (
                <div className={`displayCards${styles.classNames}`} style={styles.css} id={styles.id}>
                    {styles.childBackground.isActive && (
                        <div style={styles.childBackground.css}/>
                    )}
                    <div className="container displayCards__headerContainer">
                        {header && ((header.fieldTxtHeadline) || (header.fieldHtmlBody && header.fieldHtmlBody.processed)) && (
                            <div className="displayCards__header">
                                {header.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h2 dangerouslySetInnerHTML={{__html: filterHeadline(header.fieldTxtHeadline)}} />
                                )}
                                {header.fieldHtmlBody && header.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(header.fieldHtmlBody.processed)}}/>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="container displayCards__carouselContainer">

                        {field.fieldInnerFeaturedItems && field.fieldVarDisplayType === 'carousel' && (
                            <Slider
                                swipeToSlide
                                slidesToShow={field.fieldVarItemsPerRow < 4 ? field.fieldVarItemsPerRow : 4}
                                responsive={[
                                {
                                  breakpoint: 1024,
                                  settings: {
                                    slidesToShow: field.fieldVarItemsPerRow < 3 ? field.fieldVarItemsPerRow : 3,
                                    slidesToScroll: field.fieldVarItemsPerRow < 3 ? field.fieldVarItemsPerRow : 3,
                                    infinite: true,
                                  }
                                },
                                {
                                  breakpoint: 768,
                                  settings: {
                                    slidesToShow: field.fieldVarItemsPerRow < 2 ? field.fieldVarItemsPerRow : 2,
                                    slidesToScroll: field.fieldVarItemsPerRow < 2 ? field.fieldVarItemsPerRow : 2
                                  }
                                },
                                {
                                  breakpoint: 560,
                                  settings: {
                                    slidesToShow: 1,
                                    slidesToScroll: 1
                                  }
                                }

                              ]}>
                                {field.fieldInnerFeaturedItems.map(component => (
                                        <Paragraph key={`${id}_${component.targetRevisionId}`}
                                                   id={String(component.targetRevisionId)}
                                                   handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </Slider>
                        )}

                        {field.fieldInnerFeaturedItems && field.fieldVarDisplayType === 'grid' && (
                            <div className="grid">
                                {field.fieldInnerFeaturedItems.map(component => (
                                    <Paragraph key={`${id}_${component.targetRevisionId}`}
                                               id={String(component.targetRevisionId)}
                                               stylesObject={{minHeight: field.fieldCardMinimumHeight ? field.fieldCardMinimumHeight : null}}
                                               handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </div>
                        )}

                        {footer && ((footer.fieldTxtHeadline) || (footer.fieldObjectCta && footer.fieldObjectCta.length) || (footer.fieldHtmlBody && footer.fieldHtmlBody.processed)) && (
                            <div className="displayCards__footer">
                                {footer.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h4 dangerouslySetInnerHTML={{__html: filterHeadline(footer.fieldTxtHeadline)}} />
                                )}
                                {footer.fieldHtmlBody && footer.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(footer.fieldHtmlBody.processed)}}/>
                                )}
                                {footer.fieldObjectCta && footer.fieldObjectCta.map(component => (
                                    <Paragraph key={`${id}_${component.targetRevisionId}`}
                                               id={String(component.targetRevisionId)}
                                               handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }}
    </FeatureCardsQuery>
);

FeatureCardsAlt.propTypes = {
    id: PropTypes.string.isRequired,
    markComponentLoadedAs: PropTypes.func.isRequired,
    handleLoadedComponents: PropTypes.func.isRequired
};

export default FeatureCardsAlt;



// WEBPACK FOOTER //
// ./frontend/components/FeaturedCards2021/FeatureCardsAlt.jsx